import { CRAWLER_SKIP_GUARD, isExperimentCrawler } from './crawlerSkip';

export const HOMEPAGE_TO_EVERYWHERE_FLAG = 'homepage_to_everywhere';
export const EVERYWHERE_ROOT_PATH = '/everywhere/';
/**
 * Variant persisted after a real in-deadline assignment. The head script
 * reads it to redirect returning treatment visitors before the body parses.
 */
export const HOMEPAGE_TO_EVERYWHERE_COOKIE = 'homepage_to_everywhere_variant';
export const HOMEPAGE_TO_EVERYWHERE_COOKIE_MAX_AGE_S = 60 * 60 * 24 * 30;
/**
 * How long the mounted homepage keeps polling Mixpanel for a first-visit
 * assignment. A variant that arrives later is neither applied nor recorded,
 * so the visitor stays on the homepage and is counted in neither arm.
 */
export const HOMEPAGE_BOOT_TIMEOUT_MS = 400;
export const HOMEPAGE_FLAG_POLL_MS = 25;
/**
 * Fallback key passed to the SDK. A resolved flag that is missing or disabled
 * returns this key, which records no exposure and assigns no variant.
 */
export const HOMEPAGE_UNASSIGNED_KEY = 'unassigned';

export type HomepageToEverywhereVariant = 'control' | 'treatment';

/** Gatsby `trailingSlash: 'always'` serves the index page as `/`. */
export const isHomepagePath = (pathname: string): boolean => pathname === '/';

export const homepageVariantFromFlag = (
  value: unknown,
): HomepageToEverywhereVariant =>
  value === 'treatment' ? 'treatment' : 'control';

/**
 * A real assignment from the flag, or `null` when the flag is missing,
 * disabled, or returned something that is not a variant.
 */
export const homepageAssignmentFromFlag = (
  value: unknown,
): HomepageToEverywhereVariant | null =>
  value === 'treatment' || value === 'control' ? value : null;

/**
 * QA override, same convention as the PIP experiment: the flag key as a
 * query param. `?homepage_to_everywhere=treatment|control` skips Mixpanel.
 */
export const homepageVariantOverride = (
  search: string,
): HomepageToEverywhereVariant | null => {
  const value = new URLSearchParams(search).get(HOMEPAGE_TO_EVERYWHERE_FLAG);
  return value === 'treatment' || value === 'control' ? value : null;
};

/** Variant persisted by an earlier in-deadline assignment, if any. */
export const homepageStoredVariant = (
  cookie: string,
): HomepageToEverywhereVariant | null => {
  const match = cookie.match(
    new RegExp(
      `(?:^|;\\s*)${HOMEPAGE_TO_EVERYWHERE_COOKIE}=(treatment|control)(?:;|$)`,
    ),
  );
  return match ? (match[1] as HomepageToEverywhereVariant) : null;
};

export const homepageVariantCookie = (
  variant: HomepageToEverywhereVariant,
  secure: boolean,
): string =>
  `${HOMEPAGE_TO_EVERYWHERE_COOKIE}=${variant}; path=/; max-age=${HOMEPAGE_TO_EVERYWHERE_COOKIE_MAX_AGE_S}; SameSite=Lax${
    secure ? '; Secure' : ''
  }`;

/** Only call after a decision was actually applied within the deadline. */
export const persistHomepageVariant = (
  variant: HomepageToEverywhereVariant,
): void => {
  try {
    document.cookie = homepageVariantCookie(
      variant,
      window.location.protocol === 'https:',
    );
  } catch {
    // Cookies blocked: the next visit assigns again.
  }
};

/**
 * Keep UTM and other query params when leaving the homepage.
 * Drops the QA override so Everywhere does not inherit it.
 * Relative so preview and local hosts stay on the same origin.
 */
export const everywhereRootRedirect = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(HOMEPAGE_TO_EVERYWHERE_FLAG);
  const query = params.toString();
  return query ? `${EVERYWHERE_ROOT_PATH}?${query}` : EVERYWHERE_ROOT_PATH;
};

/**
 * What the blocking head script does before the body paints. It only ever
 * redirects a known treatment (query override or cookie); it never hides
 * the body, so control, unassigned, ad-blocked, pre-launch, and crawler
 * visitors paint the homepage immediately.
 */
export const homepageBootAction = (input: {
  pathname: string;
  search: string;
  cookie?: string;
  userAgent?: string;
}): 'redirect' | 'show' => {
  if (!isHomepagePath(input.pathname)) return 'show';
  if (isExperimentCrawler(input.userAgent)) return 'show';
  const override = homepageVariantOverride(input.search);
  if (override === 'treatment') return 'redirect';
  if (override === 'control') return 'show';
  return homepageStoredVariant(input.cookie || '') === 'treatment'
    ? 'redirect'
    : 'show';
};

/**
 * Inline head script. A treatment override or treatment cookie redirects
 * before the body is parsed. Everything else falls through to normal paint.
 */
export const HOMEPAGE_BOOT_SCRIPT = `(function(){try{if(location.pathname!=='/')return;${CRAWLER_SKIP_GUARD}var params=new URLSearchParams(location.search);var forced=params.get('${HOMEPAGE_TO_EVERYWHERE_FLAG}');if(forced==='control')return;var stored=(document.cookie.match(/(?:^|;\\s*)${HOMEPAGE_TO_EVERYWHERE_COOKIE}=(treatment|control)(?:;|$)/)||[])[1];if(forced==='treatment'||stored==='treatment'){params.delete('${HOMEPAGE_TO_EVERYWHERE_FLAG}');var q=params.toString();location.replace('${EVERYWHERE_ROOT_PATH}'+(q?'?'+q:''));}}catch(e){}})();`;

export const decideHomepageRedirect = (input: {
  pathname: string;
  search: string;
  variantKey: unknown;
}): { variant: HomepageToEverywhereVariant; redirectTo: string | null } => {
  if (!isHomepagePath(input.pathname)) {
    return { variant: 'control', redirectTo: null };
  }
  const variant = homepageVariantFromFlag(input.variantKey);
  return {
    variant,
    redirectTo:
      variant === 'treatment' ? everywhereRootRedirect(input.search) : null,
  };
};

export type HomepageNavigationDecision = {
  /** Where to `location.replace`, or `null` to stay on the homepage. */
  redirectTo: string | null;
  /** A fresh flag assignment to persist. Overrides and cookies assign nothing. */
  assigned: HomepageToEverywhereVariant | null;
  /** Flags were not loaded yet; the caller may retry before its deadline. */
  pending: boolean;
};

const stay = (
  assigned: HomepageToEverywhereVariant | null = null,
  pending = false,
): HomepageNavigationDecision => ({ redirectTo: null, assigned, pending });

/**
 * Synchronous decision shared by the client route hook and the gate. It
 * only reads the flag when neither the override nor the cookie answers,
 * because that read records `$experiment_started`; the caller must apply
 * whatever comes back. `readFlagVariant` returns `null` while flags are
 * still loading.
 */
export const resolveHomepageNavigation = (input: {
  pathname: string;
  search: string;
  cookie: string;
  userAgent: string;
  readFlagVariant: () => unknown;
}): HomepageNavigationDecision => {
  if (!isHomepagePath(input.pathname)) return stay();
  if (isExperimentCrawler(input.userAgent)) return stay();

  const override = homepageVariantOverride(input.search);
  if (override === 'treatment') {
    return {
      redirectTo: everywhereRootRedirect(input.search),
      assigned: null,
      pending: false,
    };
  }
  if (override === 'control') return stay();

  const stored = homepageStoredVariant(input.cookie);
  if (stored === 'treatment') {
    return {
      redirectTo: everywhereRootRedirect(input.search),
      assigned: null,
      pending: false,
    };
  }
  if (stored === 'control') return stay();

  const value = input.readFlagVariant();
  if (value === null || value === undefined) return stay(null, true);

  const assigned = homepageAssignmentFromFlag(value);
  if (assigned === 'treatment') {
    return {
      redirectTo: everywhereRootRedirect(input.search),
      assigned,
      pending: false,
    };
  }
  return stay(assigned);
};
